import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import Icon from '../../../components/AppIcon';
import { astraAPI } from '../../../services/astraApi';

/**
 * Asset Allocation Chart Component
 *
 * 🔧 PRODUCTION VERSION (2025-10-27): Real Position Data
 *
 * Issues Fixed:
 * 1. Removed 100% hardcoded allocationData and sectorData (Lines 8-23 in old version)
 * 2. Integrated with /portfolio/positions API
 * 3. Filtered to only show ENABLED asset classes
 * 4. Removed Options, Futures, Commodities (disabled)
 * 5. Dynamic calculation from real position values
 */

const AssetAllocationChart = () => {
  const [viewType, setViewType] = useState('pie');
  const [allocationData, setAllocationData] = useState([]);
  const [sectorData, setSectorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [systemConfig, setSystemConfig] = useState(null);

  useEffect(() => {
    fetchAllocationData();
  }, []);

  const fetchAllocationData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch system config and positions
      const config = await astraAPI.getSystemConfig();
      const positions = await astraAPI.getPortfolioAnalytics();
      const portfolio = await astraAPI.getPortfolioSummary();

      setSystemConfig(config);

      console.log('📊 Asset Allocation Chart Data:', {
        positionsCount: positions?.items?.length || 0,
        enabledClasses: config.enabled_names,
        totalValue: portfolio.total_value
      });

      if (positions && positions.items && portfolio) {
        // Calculate allocation from real positions
        const enabledNames = config?.enabled_names || ['Indian Equities', 'Crypto Spot'];
        const categorized = categorizeAssets(positions.items, enabledNames);

        const totalValue = portfolio.total_value;
        const newAllocationData = [];

        // Colors for enabled classes only
        const colors = {
          'Indian Equities': '#3B82F6',
          'Crypto Spot': '#10B981'
        };

        // Build allocation data
        Object.entries(categorized).forEach(([category, data]) => {
          if (data.totalValue > 0 && enabledNames.includes(category)) {
            const percentage = (data.totalValue / totalValue * 100).toFixed(1);
            newAllocationData.push({
              name: category,
              value: parseFloat(percentage),
              amount: data.totalValue,
              color: colors[category] || '#6B7280'
            });
          }
        });

        setAllocationData(newAllocationData);

        // Calculate sector breakdown for Indian Equities
        const sectorBreakdown = calculateSectorBreakdown(positions.items, totalValue);
        setSectorData(sectorBreakdown);

      } else {
        throw new Error('Failed to fetch allocation data');
      }

    } catch (err) {
      console.error('Failed to fetch asset allocation chart data:', err);
      setError('Failed to load allocation chart');
      setAllocationData([]);
      setSectorData([]);
    } finally {
      setLoading(false);
    }
  };

  const categorizeAssets = (positions, enabledNames) => {
    const categories = {};

    // Initialize only enabled categories
    enabledNames.forEach(name => {
      categories[name] = { positions: [], totalValue: 0, totalPnl: 0 };
    });

    positions.forEach(position => {
      const symbol = position.symbol;
      const value = position.current_value || 0;
      const pnl = position.unrealized_pnl || 0;

      // Categorize based on symbol
      if ((symbol.includes('.NS') || symbol.includes('.BSE')) && enabledNames.includes('Indian Equities')) {
        categories['Indian Equities'].positions.push(position);
        categories['Indian Equities'].totalValue += value;
        categories['Indian Equities'].totalPnl += pnl;
      } else if ((symbol.includes('USDT') || symbol.includes('BTC') || symbol.includes('ETH')) && enabledNames.includes('Crypto Spot')) {
        categories['Crypto Spot'].positions.push(position);
        categories['Crypto Spot'].totalValue += value;
        categories['Crypto Spot'].totalPnl += pnl;
      }
    });

    return categories;
  };

  const calculateSectorBreakdown = (positions, totalValue) => {
    // Simple sector mapping for Indian stocks
    const sectorMap = {
      'RELIANCE': 'Energy',
      'TCS': 'Technology',
      'INFY': 'Technology',
      'HDFCBANK': 'Banking',
      'ICICIBANK': 'Banking',
      'ITC': 'Consumer',
      'BHARTIARTL': 'Telecom',
      'HINDUNILVR': 'Consumer'
    };

    const sectors = {};

    positions.forEach(position => {
      const symbol = position.symbol.replace('.NS', '').replace('.BSE', '');
      const sector = sectorMap[symbol] || 'Others';
      const value = position.current_value || 0;

      if (!sectors[sector]) {
        sectors[sector] = { allocation: 0, amount: 0 };
      }

      sectors[sector].amount += value;
      sectors[sector].allocation = (sectors[sector].amount / totalValue * 100);
    });

    // Convert to array format
    return Object.entries(sectors).map(([sector, data]) => ({
      sector,
      allocation: parseFloat(data.allocation.toFixed(1)),
      amount: data.amount
    })).sort((a, b) => b.allocation - a.allocation);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevated">
          <p className="text-sm font-medium text-popover-foreground mb-1">{data?.name || data?.sector}</p>
          <p className="text-sm text-muted-foreground">
            {data?.value || data?.allocation}% • ₹{data?.amount?.toLocaleString('en-IN')}
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return percent > 0.05 ? (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight={500}
      >
        {`${(percent * 100)?.toFixed(0)}%`}
      </text>
    ) : null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="PieChart" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Asset Allocation</h3>
            <p className="text-sm text-muted-foreground">Portfolio distribution by asset class</p>
          </div>
        </div>

        <div className="flex bg-muted rounded-lg p-1">
          <button
            onClick={() => setViewType('pie')}
            className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm font-medium transition-smooth ${
              viewType === 'pie' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="PieChart" size={14} />
            <span>Pie</span>
          </button>
          <button
            onClick={() => setViewType('bar')}
            className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm font-medium transition-smooth ${
              viewType === 'bar' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="BarChart3" size={14} />
            <span>Bar</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="h-80 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="h-80 flex items-center justify-center">
          <div className="text-center">
            <Icon name="AlertTriangle" size={24} className="text-error mx-auto mb-2" />
            <p className="text-sm text-muted-foreground mb-3">{error}</p>
            <button
              onClick={fetchAllocationData}
              className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chart */}
          <div className="lg:col-span-2">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                {viewType === 'pie' ? (
                  <PieChart>
                    <Pie
                      data={allocationData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={<CustomLabel />}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {allocationData?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry?.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                ) : (
                  <BarChart data={allocationData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis
                      dataKey="name"
                      stroke="var(--color-muted-foreground)"
                      fontSize={12}
                    />
                    <YAxis
                      stroke="var(--color-muted-foreground)"
                      fontSize={12}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {allocationData?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry?.color} />
                      ))}
                    </Bar>
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>

          {/* Legend & Stats */}
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3">Asset Classes</h4>
              <div className="space-y-2">
                {allocationData?.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex items-center space-x-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: item?.color }}
                      ></div>
                      <span className="text-sm text-foreground">{item?.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-foreground">{item?.value}%</div>
                      <div className="text-xs text-muted-foreground">₹{(item?.amount / 100000).toFixed(1)}L</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {sectorData.length > 0 && (
              <div className="pt-4 border-t border-border">
                <h4 className="text-sm font-semibold text-foreground mb-3">Sector Breakdown</h4>
                <div className="space-y-2">
                  {sectorData.slice(0, 5)?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{item?.sector}</span>
                      <span className="font-medium text-foreground">{item?.allocation}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetAllocationChart;
